/**
 * Customizations include options and modifiers which can later be applied to products.
 * Options are designed to add variations to a product, where modifiers add a customizable change to the product but without creating another variant.
 */
export interface Customization extends CustomizationCustomizationSettingsOneOf {
  /**
   * Free text input settings.
   *
   * > **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.
   * @immutable
   */
  freeTextInput?: FreeTextSettings;
  /**
   * Choices settings.
   *
   * > **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.
   */
  choicesSettings?: ChoicesSettings;
  /**
   * Customization ID.
   * @minLength 1
   * @maxLength 36
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   *
   * Ignored when creating a customization.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the customization was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the customization was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * A read-only identifier generated from the customization name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string | null;
  /**
   * Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Customization type.
   * @immutable
   */
  customizationType?: CustomizationTypeWithLiterals;
  /**
   * Customization render type.
   *
   * Defines how the customization will be displayed in the storefront.
   * @immutable
   */
  customizationRenderType?: CustomizationRenderTypeWithLiterals;
  /**
   * Number of products this customization is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.
   * @readonly
   */
  assignedProductsCount?: number | null;
}

/** @oneof */
export interface CustomizationCustomizationSettingsOneOf {
  /**
   * Free text input settings.
   *
   * > **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.
   * @immutable
   */
  freeTextInput?: FreeTextSettings;
  /**
   * Choices settings.
   *
   * > **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.
   */
  choicesSettings?: ChoicesSettings;
}

export enum CustomizationType {
  PRODUCT_OPTION = 'PRODUCT_OPTION',
  MODIFIER = 'MODIFIER',
}

/** @enumType */
export type CustomizationTypeWithLiterals =
  | CustomizationType
  | 'PRODUCT_OPTION'
  | 'MODIFIER';

export enum CustomizationRenderType {
  /** Customization by free text input. */
  FREE_TEXT = 'FREE_TEXT',
  /** Customization by selection of text choices. */
  TEXT_CHOICES = 'TEXT_CHOICES',
  /** Customization by selection of colors. */
  SWATCH_CHOICES = 'SWATCH_CHOICES',
}

/** @enumType */
export type CustomizationRenderTypeWithLiterals =
  | CustomizationRenderType
  | 'FREE_TEXT'
  | 'TEXT_CHOICES'
  | 'SWATCH_CHOICES';

export interface FreeTextSettings {
  /** Minimum text character length. */
  minCharCount?: number;
  /**
   * Maximum text character length.
   * @max 500
   */
  maxCharCount?: number;
  /**
   * Default amount added to a product's price when this choice is assigned to a modifier.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  defaultAddedPrice?: string | null;
  /**
   * Title to display to customer for their free-text input.
   * @minLength 1
   * @maxLength 100
   */
  title?: string;
  /**
   * A read-only identifier generated from the title.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 150
   */
  key?: string | null;
}

/** Settings for choice-based customizations. */
export interface ChoicesSettings {
  /**
   * List of choices.
   * @minSize 1
   * @maxSize 200
   * @immutable
   */
  choices?: Choice[];
}

export interface Choice extends ChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   * @format COLOR_HEX
   */
  colorCode?: string;
  /**
   * Choice ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Choice type.
   *
   * > **Notes:**
   * > + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.
   * > + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.
   * @immutable
   */
  choiceType?: ChoiceTypeWithLiterals;
  /**
   * A read-only identifier generated from the choice name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string | null;
  /**
   * Choice name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Default amount added to a product's price when this customization is assigned to a modifier.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  defaultAddedPrice?: string | null;
}

/** @oneof */
export interface ChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   * @format COLOR_HEX
   */
  colorCode?: string;
}

export enum ChoiceType {
  /** For a `customizationRenderType` of `TEXT_CHOICES`. */
  CHOICE_TEXT = 'CHOICE_TEXT',
  /** For `customizationRenderType: SWATCH_CHOICES`. */
  ONE_COLOR = 'ONE_COLOR',
}

/** @enumType */
export type ChoiceTypeWithLiterals = ChoiceType | 'CHOICE_TEXT' | 'ONE_COLOR';

export interface MultipleColors {
  /**
   * A list of color codes.
   * @minLength 3
   * @maxLength 20
   * @minSize 2
   * @maxSize 2
   */
  colorCodes?: string[];
}

export interface Image {
  /** WixMedia image ID. */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /** Image alt text. */
  altText?: string | null;
  /**
   * Image filename.
   * @readonly
   */
  filename?: string | null;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export interface CommonImage {
  /**
   * WixMedia image ID.
   * @maxLength 400
   */
  id?: string;
  /**
   * Image URL.
   * @format WEB_URL
   */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /**
   * Image alt text.
   * @maxLength 200
   */
  altText?: string | null;
  /**
   * Image filename.
   * @readonly
   * @maxLength 200
   */
  filename?: string | null;
}

export enum ChoiceSortStrategy {
  /** Default. Text choices are sorted by name, swatch choices are sorted by product count. */
  DEFAULT = 'DEFAULT',
  /** Alphabetically by name, case-insensitive. Numeric values are sorted first. */
  BY_NAME = 'BY_NAME',
  /** By the number of products assigned to each choice, in descending order. */
  BY_PRODUCT_COUNT = 'BY_PRODUCT_COUNT',
  /** By the order of the choices list. Use [Reorder Customization Choices](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/reorder-customization-choices) to set the order. */
  MANUAL = 'MANUAL',
}

/** @enumType */
export type ChoiceSortStrategyWithLiterals =
  | ChoiceSortStrategy
  | 'DEFAULT'
  | 'BY_NAME'
  | 'BY_PRODUCT_COUNT'
  | 'MANUAL';

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateCustomizationRequest {
  /** Customization to create. */
  customization: Customization;
}

export interface CreateCustomizationResponse {
  /** Created customization. */
  customization?: Customization;
}

export interface GetCustomizationRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  /** Assigned products count. */
  ASSIGNED_PRODUCTS_COUNT = 'ASSIGNED_PRODUCTS_COUNT',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'ASSIGNED_PRODUCTS_COUNT';

export interface GetCustomizationResponse {
  /** Customization. */
  customization?: Customization;
}

export interface UpdateCustomizationRequest {
  /** Customization to update. */
  customization: Customization;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateCustomizationResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface DeleteCustomizationRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
}

export interface DeleteCustomizationResponse {}

export interface QueryCustomizationsRequest {
  /** Query options. */
  query?: CursorQuery;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryCustomizationsResponse {
  /**
   * List of customizations.
   * @minSize 1
   * @maxSize 200
   */
  customizations?: Customization[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateCustomizationsRequest {
  /**
   * Customizations to create.
   * @minSize 1
   * @maxSize 100
   */
  customizations: Customization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateCustomizationsResponse {
  /**
   * Customizations created by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCustomizationResult {
  /** Bulk action metadata for customization. */
  itemMetadata?: ItemMetadata;
  /**
   * Full customization entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  customization?: Customization;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @minLength 1
   * @maxLength 36
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface CustomizationIdsWrapper {
  /**
   * list of all the customization ids that are invalid
   * e.g list of failed choices ids
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 36
   */
  customizationIds?: string[];
}

export interface BulkCreateCustomizationsForMigrationRequest {
  /**
   * Customizations to create.
   * @minSize 1
   * @maxSize 100
   */
  customizations?: Customization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkCreateCustomizationsForMigrationResponse {
  /**
   * Customizations created by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface AddCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Choices to add.
   * @minSize 1
   * @maxSize 100
   */
  choices: Choice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface AddCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface SetCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Choices to set.
   * @minSize 1
   * @maxSize 200
   */
  choices: Choice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface SetCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface RemoveCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * IDs of choices to remove.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  choiceIds: string[];
  /** Customization revision. */
  revision?: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface RemoveCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface UpdateCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   */
  revision: string;
  /**
   * Choices to update. Each item must include a `choice` with a valid `id` matching an existing choice in the customization.
   * @minSize 1
   * @maxSize 100
   */
  choices: MaskedChoice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedChoice {
  /** Choice to update. */
  choice?: Choice;
}

export interface UpdateCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface ReorderCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /** Revision number, which increments by 1 each time the customization is updated. To prevent conflicting changes, the current revision must be passed when updating the customization. */
  revision: string;
  /**
   * Choice IDs in the desired display order. Must contain exactly the same set of IDs as the customization's current choices — no additions or removals.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  choiceIds: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface ReorderCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

/** Additional error details returned with the `CHOICE_IDS_MISMATCH` error. */
export interface ChoiceIdsMismatchData {
  /** Choice IDs that exist in the customization but are missing from the request. */
  missingIds?: string[];
  /** Choice IDs included in the request that don't exist in the customization. */
  extraIds?: string[];
}

export interface BulkAddCustomizationChoicesRequest {
  /**
   * List of customization IDs and choices.
   * @minSize 1
   * @maxSize 100
   */
  customizationsChoices: CustomizationChoices[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CustomizationChoices {
  /**
   * Customization ID.
   * @minLength 1
   * @maxLength 36
   */
  customizationId?: string;
  /**
   * Choices to add.
   * @minSize 1
   * @maxSize 100
   */
  choices?: Choice[];
}

export interface BulkAddCustomizationChoicesResponse {
  /**
   * Customizations updated by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateCustomizationsRequest {
  /**
   * List of customizations to update.
   * @minSize 1
   * @maxSize 100
   */
  customizations: MaskedCustomization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedCustomization {
  /** Customization to update. */
  customization?: Customization;
}

export interface BulkUpdateCustomizationsResponse {
  /**
   * Customizations updated by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface RecloneSiteRequest {
  /** @format GUID */
  cloneFrom?: string;
}

export interface RecloneSiteResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateCustomizationApplicationErrors =
  | {
      code?: 'OPTIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MODIFIERS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_CUSTOMIZATION_RENDER_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_FREE_TEXT_SETTINGS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateCustomizationValidationErrors =
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'CUSTOMIZATION_WITH_ONLY_ID_OR_CHOICE_IDS';
    }
  | {
      ruleName?: 'MIXED_CHOICE_IDS_CONSISTENCY';
    }
  | {
      ruleName?: 'ID_NOT_ALLOWED_IN_REQUEST';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type DeleteCustomizationValidationErrors = {
  ruleName?: 'NOT_ALLOWED_DELETE_CUSTOMIZATION_ASSIGNED_TO_PRODUCT';
};
/** @docsIgnore */
export type BulkCreateCustomizationsApplicationErrors =
  | {
      code?: 'OPTIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MODIFIERS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    };
/** @docsIgnore */
export type BulkCreateCustomizationsValidationErrors =
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    }
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'CUSTOMIZATION_WITH_ONLY_ID_OR_CHOICE_IDS';
    }
  | {
      ruleName?: 'MIXED_CHOICE_IDS_CONSISTENCY';
    }
  | {
      ruleName?: 'ID_NOT_ALLOWED_IN_REQUEST';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type AddCustomizationChoicesApplicationErrors =
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type SetCustomizationChoicesApplicationErrors =
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    };
/** @docsIgnore */
export type SetCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'NOT_ALLOWED_DELETE_CHOICE_ASSIGNED_TO_PRODUCT';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type RemoveCustomizationChoicesApplicationErrors = {
  code?: 'CHOICE_ASSIGNED_TO_PRODUCT';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'NOT_ALLOWED_CUSTOMIZATION_WITHOUT_CHOICES';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_DELETE_REFERENCED';
    };
/** @docsIgnore */
export type UpdateCustomizationChoicesApplicationErrors = {
  code?: 'CHOICE_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'UNSUPPORTED_CHOICE_FIELD';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type ReorderCustomizationChoicesApplicationErrors =
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICE_IDS_MISMATCH';
      description?: string;
      data?: ChoiceIdsMismatchData;
    };
/** @docsIgnore */
export type BulkAddCustomizationChoicesApplicationErrors =
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkAddCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
